import React, { useCallback, useMemo } from 'react';
import { Form } from 'react-bootstrap';
import AsyncSelect from 'react-select/async';
import { buscarPropuestas } from '../api/common';

const baseStyles = {
  menu: (base) => ({
    ...base,
    width: 'max-content',
    minWidth: '100%',
    zIndex: 9999,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
};

const mapPropuestaToOption = (prop) => ({
  value: prop._id,
  label: `${prop.consecutivo} · ${prop.cliente_nombre} · ${prop.cliente_nit}`,
  data: prop,
});

/**
 * Selector reusable de propuestas comerciales con búsqueda asíncrona.
 * Requiere mínimo 3 caracteres. Al seleccionar, llama onChange con el objeto propuesta.
 *
 * @param {Object|null} value - Propuesta seleccionada (objeto completo) o null
 * @param {Function} onChange - Callback (propuestaData | null)
 * @param {HTMLElement} [menuPortalTarget] - Elemento destino del menú (normalmente document.body)
 */
const PropuestaComercialSearchSelect = ({
  value = null,
  onChange,
  menuPortalTarget,
}) => {
  const selectStyles = useMemo(() => ({ ...baseStyles }), []);

  // Build internal option from external value object
  const selectValue = value ? mapPropuestaToOption(value) : null;

  const loadOptions = useCallback(async (inputValue) => {
    const term = (inputValue || '').trim();
    if (term.length < 3) return [];
    try {
      const response = await buscarPropuestas(term);
      const propuestas = response?.data?.data || [];
      return propuestas.map(mapPropuestaToOption);
    } catch {
      return [];
    }
  }, []);

  const handleChange = (option) => {
    onChange(option?.data || null);
  };

  const noOptionsMessage = ({ inputValue }) => {
    if (!inputValue || inputValue.trim().length < 3) return 'Escriba al menos 3 caracteres...';
    return 'Sin resultados';
  };

  return (
    <Form.Group className="mb-3">
      <Form.Label className="bold">Propuesta Comercial</Form.Label>
      <AsyncSelect
        value={selectValue}
        onChange={handleChange}
        placeholder="Buscar por consecutivo, cliente o NIT..."
        isClearable
        menuPortalTarget={menuPortalTarget}
        menuPosition="fixed"
        styles={selectStyles}
        noOptionsMessage={noOptionsMessage}
        loadingMessage={() => 'Buscando...'}
        cacheOptions={false}
        defaultOptions={false}
        loadOptions={loadOptions}
        isSearchable
      />
    </Form.Group>
  );
};

export default PropuestaComercialSearchSelect;
